import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchSubscriptionInvoices } from "../services/subscriptionsService";
import type { SubscriptionInvoice } from "../types/subscriptions.types";

export type BillingHistoryStatus = "loading" | "ready" | "failed";

export interface BillingHistoryState {
  /** Every invoice the workspace has been raised, newest first. */
  invoices: SubscriptionInvoice[];
  status: BillingHistoryStatus;
  error?: string;
  reload: () => void;
}

const EMPTY: SubscriptionInvoice[] = [];

export const BILLING_HISTORY_QUERY_KEY = ["billing", "invoices"] as const;

/**
 * What the workspace has been charged, invoice by invoice.
 *
 * An empty list is an ordinary answer, not a failure: a subscription bought
 * minutes ago has no invoice behind it until the first charge settles, and the
 * table says so in a line rather than looking broken.
 *
 * It asks `GET /billing/invoices/` outright, which answers in DRF's page
 * envelope. It used to be held behind `SUBSCRIPTIONS_LIVE`, because the
 * endpoint it read did not exist; this one does.
 *
 * Backed by TanStack Query: a screen visited again shows the last-read
 * invoices immediately while a fresh read checks it behind the scenes, rather
 * than making the Billing tab wait on the network every time it opens.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useBillingHistory(): BillingHistoryState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: BILLING_HISTORY_QUERY_KEY,
    queryFn: ({ signal }) => fetchSubscriptionInvoices(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) {
      console.error("billing history failed to load:", query.error);
    }
  }, [query.isError, query.error]);

  return {
    invoices: query.data ?? EMPTY,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error:
      query.isError && query.error instanceof ApiError
        ? query.error.message
        : query.isError
        ? "We could not reach the server. Check your connection and try again."
        : undefined,
    reload: () => {
      void query.refetch();
    },
  };
}

export default useBillingHistory;
